import { AuthState } from '@app/infrastructure/auth/model/AuthState';
import { Address } from '@app/domain/model/Address';
import { useCallback, useMemo, useState, ChangeEvent } from 'react';
import { useChangeEffect } from '@app/lib/use-change-effect/useChangeEffect';
import { formatLithuanianPhone } from '@app/lib/phone/formatLithuanianPhone';

export interface PolicyholderFormFields {
    firstName: string;
    lastName: string;
    address: Nullable<Address>;
    phoneNumber: string;
    email: string;
    emailConfirmation: string;
    isInsuredPerson: boolean;
}

export interface PolicyholderFormHandlers {
    handleFirstNameChange: (e: ChangeEvent<HTMLInputElement>) => void;
    handleLastNameChange: (e: ChangeEvent<HTMLInputElement>) => void;
    handleAddressChange: (address: Nullable<Address>) => void;
    handlePhoneNumberChange: (e: ChangeEvent<HTMLInputElement>) => void;
    handleEmailChange: (e: ChangeEvent<HTMLInputElement>) => void;
    handleEmailConfirmationChange: (e: ChangeEvent<HTMLInputElement>) => void;
    handleIsInsuredPersonChange: (e: ChangeEvent<HTMLInputElement>) => void;
}

export function usePolicyholderFormFields(authState: AuthState) {
    const selectedClient = authState.selectedClient;
    const profile = authState.profile;

    const initialFields = useMemo<PolicyholderFormFields>(() => {
        const profileAddress = profile?.mainAddress ?? profile?.addresses?.[0] ?? null;
        return {
            firstName: selectedClient?.firstName ?? profile?.firstName ?? '',
            lastName: selectedClient?.lastName ?? profile?.name ?? '',
            address: profileAddress,
            phoneNumber: (profile?.mainPhone ?? '').replace(/^\+370/, ''),
            email: profile?.mainEmail ?? '',
            emailConfirmation: profile?.mainEmail ?? '',
            isInsuredPerson: true,
        };
    }, [
        profile?.addresses,
        profile?.firstName,
        profile?.mainAddress,
        profile?.mainEmail,
        profile?.mainPhone,
        profile?.name,
        selectedClient?.firstName,
        selectedClient?.lastName,
    ]);

    const [fields, setFields] = useState<PolicyholderFormFields>(initialFields);

    useChangeEffect(() => {
        const profileAddress = profile?.mainAddress ?? profile?.addresses?.[0] ?? null;
        setFields(prevFields => ({
            ...prevFields,
            firstName: selectedClient?.firstName ?? profile?.firstName ?? prevFields.firstName,
            lastName: selectedClient?.lastName ?? profile?.name ?? prevFields.lastName,
            address: profileAddress ?? prevFields.address,
            phoneNumber: (profile?.mainPhone ?? '').replace(/^\+370/, '') || prevFields.phoneNumber,
            email: profile?.mainEmail ?? prevFields.email,
            emailConfirmation: profile?.mainEmail ?? prevFields.emailConfirmation,
        }));
    }, profile);

    const handleFirstNameChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        setFields(prevFields => ({ ...prevFields, firstName: e.target.value }));
    }, []);

    const handleLastNameChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        setFields(prevFields => ({ ...prevFields, lastName: e.target.value }));
    }, []);

    const handleAddressChange = useCallback((address: Nullable<Address>) => {
        setFields(prevFields => ({ ...prevFields, address }));
    }, []);

    const handlePhoneNumberChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        const formattedValue = formatLithuanianPhone(e.target.value);
        setFields(prevFields => ({ ...prevFields, phoneNumber: formattedValue }));
    }, []);

    const handleEmailChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        setFields(prevFields => ({ ...prevFields, email: e.target.value }));
    }, []);

    const handleEmailConfirmationChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        setFields(prevFields => ({ ...prevFields, emailConfirmation: e.target.value }));
    }, []);

    const handleIsInsuredPersonChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        setFields(prevFields => ({ ...prevFields, isInsuredPerson: e.target.checked }));
    }, []);

    const handlers: PolicyholderFormHandlers = {
        handleFirstNameChange,
        handleLastNameChange,
        handleAddressChange,
        handlePhoneNumberChange,
        handleEmailChange,
        handleEmailConfirmationChange,
        handleIsInsuredPersonChange,
    };

    return {
        fields,
        handlers,
        setFields,
    };
}
